import { useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Skeleton, Tooltip } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAssetDetail } from '../api/hooks';
import { useMultiMarketProbability } from '../api/useMultiMarketProbability';
import { Badge, SectionCard, PanelHeader } from '../components/shared';
import { PriceChart, CHART_MARGIN_LEFT } from '../components/charts/PriceChart';
import type { TimeRange } from '../components/charts/PriceChart';
import { SignalSubscribeButton } from '../../../notifications/SignalSubscribeButton';
import { pmSignalForTicker } from '../hooks/portfolioManagerSignal';
import { autoResolution } from '../components/charts/convergence';
import type { MarketLineMeta } from '../components/charts/convergence';
import { AssetTabs } from '../components/AssetTabs';
import type { EventFromAsset, MacroSensitivity } from '../api/types';
import { AssetImage } from '../components/AssetImage';
import { assignMarketColors } from '../utils/marketColors';
import { EventsPanel } from '../components/EventsPanel';
import { useAssetNews } from '../hooks/useAssetNews';
import { SidebarShell, useSidebarPanel } from '../components/SidebarShell';
import { NewsPanel } from '../components/NewsPanel';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import type { TiingoNewsArticle } from '../api/newsTypes';
import { hoverScrollbar } from '../../../styles/hoverScrollbar';
import { CONTENT_HEIGHT_SX } from '../../layout/chrome';
import { openInNewTab } from '../../../utils/newTab';

export function AssetView() {
    const { assetType, ticker } = useParams<{
        assetType: string;
        ticker: string;
    }>();
    const theme = useTheme();
    const [timeRange, setTimeRange] = useState<TimeRange>('1Y');
    const [zoomRange, setZoomRange] = useState<[Date, Date] | null>(null);

    // Clear zoom when preset range changes
    const handleTimeRangeChange = useCallback((r: TimeRange) => {
        setTimeRange(r);
        setZoomRange(null);
    }, []);

    // Probability overlay state
    const [selectedEvent, setSelectedEvent] = useState<EventFromAsset | null>(
        null
    );
    const [visibleMarketIds, setVisibleMarketIds] =
        useState<Set<string> | null>(null);

    // Live API data
    const {
        data: asset,
        loading: assetLoading,
        error: assetError,
    } = useAssetDetail(assetType || null, ticker || null);

    const assetContext = useMemo(
        () => (ticker && assetType ? { ticker, assetType } : null),
        [ticker, assetType]
    );

    const [events, setEvents] = useState<EventFromAsset[]>([]);

    const handleEventsChange = useCallback((evts: EventFromAsset[]) => {
        setEvents(evts);
    }, []);

    // Open the event detail in a new tab (same URL pattern as EventCard), so
    // the asset page keeps its chart, selection and scroll position.
    const handleEventTitleClick = useCallback(() => {
        if (!selectedEvent) return;
        const params = ticker
            ? `?asset=${encodeURIComponent(ticker)}&assetType=${encodeURIComponent(assetType || '')}&assetName=${encodeURIComponent(asset?.asset_name || ticker)}`
            : '';
        openInNewTab(
            `/markets/event/${selectedEvent.slug || selectedEvent.event_id}${params}`
        );
    }, [selectedEvent, ticker, assetType, asset?.asset_name]);

    // UI state
    const [hoveredEvent, setHoveredEvent] = useState<EventFromAsset | null>(
        null
    );
    const [showMacro, setShowMacro] = useState(false);

    // News column
    const { articles: newsArticles } = useAssetNews(ticker, timeRange);
    const [hoveredArticle, setHoveredArticle] =
        useState<TiingoNewsArticle | null>(null);

    // Sidebar panel state
    const [leftPanel, setLeftPanel] = useSidebarPanel('asset.left', 'news');
    const [selectedArticleIds, setSelectedArticleIds] = useState<Set<number>>(
        new Set()
    );

    const handleSelectArticle = useCallback(
        (article: TiingoNewsArticle | null) => {
            if (!article) return;
            setSelectedArticleIds(prev => {
                const next = new Set(prev);
                if (next.has(article.id)) next.delete(article.id);
                else next.add(article.id);
                return next;
            });
        },
        []
    );

    const selectedPinnedDates = useMemo(
        () =>
            newsArticles
                .filter(a => selectedArticleIds.has(a.id))
                .map(a => new Date(a.publishedDate)),
        [newsArticles, selectedArticleIds]
    );

    const macroSensitivities: MacroSensitivity[] =
        asset?.market_drivers?.macro_sensitivities || [];

    const RANGE_DAYS: Record<TimeRange, number> = {
        '1W': 7,
        '1M': 30,
        '3M': 90,
        '1Y': 365,
        ALL: 365 * 15,
    };
    const timeDomain = useMemo((): [Date, Date] => {
        const end = new Date();
        const start = new Date(
            end.getTime() - RANGE_DAYS[timeRange] * 24 * 60 * 60 * 1000
        );
        return [start, end];
    }, [timeRange]);

    const effectiveDomain = zoomRange || timeDomain;

    // Toggle event selection (click same event again to deselect)
    const handleEventClick = useCallback((evt: EventFromAsset) => {
        setSelectedEvent(prev =>
            prev?.event_id === evt.event_id ? null : evt
        );
        // Default: show highest-probability + highest-volume market lines
        const summaries = evt.markets_summary;
        if (summaries.length > 0) {
            const highestProb = summaries.reduce((best, m) =>
                m.yes_prob > best.yes_prob ? m : best
            );
            const highestVol = summaries.reduce((best, m) =>
                m.volume > best.volume ? m : best
            );
            const defaults = new Set<string>();
            defaults.add(highestProb.condition_id);
            defaults.add(highestVol.condition_id);
            setVisibleMarketIds(defaults);
        } else {
            setVisibleMarketIds(null);
        }
    }, []);

    // Derive condition IDs from selected event (sorted by volume desc, up to 8)
    const allConditionIds = useMemo(() => {
        if (!selectedEvent) return [];
        return [...selectedEvent.markets_summary]
            .sort((a, b) => b.volume - a.volume)
            .slice(0, 8)
            .map(m => m.condition_id);
    }, [selectedEvent]);

    const probStart = selectedEvent ? new Date(selectedEvent.start_date) : null;
    const probEnd = selectedEvent ? new Date(selectedEvent.end_date) : null;

    const resolution = useMemo(() => {
        if (!selectedEvent) return 1440;
        return autoResolution([
            new Date(selectedEvent.start_date),
            new Date(selectedEvent.end_date),
        ]);
    }, [selectedEvent]);

    const {
        series: probSeries,
        loading: probLoading,
        allLoaded: probAllLoaded,
    } = useMultiMarketProbability(
        allConditionIds,
        probStart,
        probEnd,
        resolution,
        visibleMarketIds ?? undefined
    );

    const allMarketMeta: MarketLineMeta[] = useMemo(() => {
        if (!selectedEvent || allConditionIds.length === 0) return [];
        const colorMap = assignMarketColors(allConditionIds);
        return allConditionIds.map(id => {
            const mkt = selectedEvent.markets_summary.find(
                m => m.condition_id === id
            );
            return {
                conditionId: id,
                question: mkt?.yes_outcome || mkt?.question || id,
                color: colorMap.get(id) || '#999',
            };
        });
    }, [selectedEvent, allConditionIds]);

    const isCrypto = assetType === 'cryptocurrency';

    // Per-asset portfolio-manager signal subscription (crypto BTC/ETH only).
    const pmSignal = isCrypto ? pmSignalForTicker(ticker) : null;

    // Build probability overlay prop for PriceChart
    const probabilityOverlay = useMemo(() => {
        if (!selectedEvent) return null;
        return {
            series: probSeries,
            allMarketMeta,
            visibleMarketIds,
            onVisibleMarketsChange: setVisibleMarketIds,
            loading: probLoading,
            allLoaded: probAllLoaded,
            eventStartDate: new Date(selectedEvent.start_date),
            eventEndDate: new Date(selectedEvent.end_date),
            eventTitle: selectedEvent.event_title,
        };
    }, [
        selectedEvent,
        probSeries,
        allMarketMeta,
        visibleMarketIds,
        probLoading,
        probAllLoaded,
    ]);

    if (!ticker) return null;

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    lg: '280px minmax(0px, 1320px)',
                },
                ...CONTENT_HEIGHT_SX,
                overflow: 'hidden',
            }}
        >
            {/* Left sidebar */}
            <Box
                sx={{
                    display: { xs: 'none', lg: 'block' },
                    height: '100%',
                    overflowY: 'hidden',
                    overscrollBehavior: 'contain',
                    borderRight: 1,
                    borderColor: 'divider',
                }}
            >
                <SidebarShell
                    panels={[
                        {
                            id: 'news',
                            label: 'News',
                            icon: <NewspaperIcon />,
                            disabled: newsArticles.length === 0,
                            content: (
                                <NewsPanel
                                    articles={newsArticles}
                                    ticker={ticker!}
                                    onHoverArticle={setHoveredArticle}
                                    selectedArticleIds={selectedArticleIds}
                                    onSelectArticle={handleSelectArticle}
                                />
                            ),
                        },
                    ]}
                    activePanel={leftPanel}
                    onPanelChange={setLeftPanel}
                />
            </Box>
            {/* Center content */}
            <Box
                sx={{
                    px: 2,
                    py: 2,
                    minWidth: 0,
                    height: '100%',
                    ...hoverScrollbar,
                }}
            >
                {/* Asset header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1.5,
                        px: 0.5,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {assetLoading ? (
                            <Skeleton width={200} height={28} />
                        ) : assetError ? (
                            <Typography
                                sx={{
                                    color: 'error.main',
                                    fontSize: '13px',
                                }}
                            >
                                Failed to load asset details
                            </Typography>
                        ) : (
                            <>
                                <AssetImage
                                    assetType={assetType}
                                    ticker={ticker}
                                    size={36}
                                />
                                <Typography
                                    sx={{
                                        fontSize: '18px',
                                        fontWeight: 800,
                                    }}
                                >
                                    {asset?.asset_name || ticker}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '13px',
                                        color: 'text.secondary',
                                        fontWeight: 500,
                                    }}
                                >
                                    {ticker}
                                </Typography>
                                <Badge color={theme.palette.primary.main}>
                                    {asset?.asset_type || assetType || 'asset'}
                                </Badge>
                                {asset?.classification?.sub_categories?.map(
                                    cat => (
                                        <Badge
                                            key={cat}
                                            color={theme.palette.text.secondary}
                                            bg={alpha(
                                                theme.palette.text.secondary,
                                                0.08
                                            )}
                                        >
                                            {cat}
                                        </Badge>
                                    )
                                )}
                            </>
                        )}
                    </Box>
                    {pmSignal && (
                        <SignalSubscribeButton
                            eventId={pmSignal.eventId}
                            title={pmSignal.title}
                        />
                    )}
                </Box>

                {/* Main content */}
                <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                >
                    {/* Price + Gantt synchronized charts */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                        }}
                    >
                        <PriceChart
                            ticker={ticker}
                            assetType={assetType}
                            events={events}
                            hoveredEvent={hoveredEvent}
                            timeRange={timeRange}
                            onTimeRangeChange={handleTimeRangeChange}
                            probabilityOverlay={probabilityOverlay}
                            onEventTitleClick={handleEventTitleClick}
                            zoomRange={zoomRange}
                            onZoomRangeChange={setZoomRange}
                            newsMarkerDate={
                                hoveredArticle
                                    ? new Date(hoveredArticle.publishedDate)
                                    : null
                            }
                            newsPinnedDates={selectedPinnedDates}
                        />
                    </Box>

                    <EventsPanel
                        timeDomain={effectiveDomain}
                        hoveredEvent={hoveredEvent}
                        onHoverChange={setHoveredEvent}
                        selectedEvent={selectedEvent}
                        onSelectEvent={handleEventClick}
                        chartMarginLeft={CHART_MARGIN_LEFT}
                        onZoomRangeChange={setZoomRange}
                        futureOverhangPx={CHART_MARGIN_LEFT - 5}
                        assetContext={{
                            ticker: ticker!,
                            assetType: assetType || '',
                            name: asset?.asset_name || ticker!,
                        }}
                        topMinHeight={400}
                        onEventsChange={handleEventsChange}
                    />

                    {/* Asset analysis tabs */}
                    <AssetTabs
                        ticker={ticker}
                        assetType={assetType}
                        data={asset?.data}
                        loading={assetLoading}
                        error={assetError}
                    />
                </Box>
            </Box>
        </Box>
    );
}
